/**
 * Coarse, non-identifying platform context for camera diagnostics.
 *
 * Camera behaviour differs materially between WebKit on iOS (Safari and every
 * other iOS browser share the same engine), Android browsers, and desktop.
 * These fields let camera telemetry be segmented by device family without
 * transporting the raw user agent on every event; the values are derived
 * from information the browser already exposes and carry no per-user
 * identity.
 */

export type CameraPlatformFamily = 'ios' | 'android' | 'desktop' | 'other';

export type CameraBrowserFamily =
  | 'safari'
  | 'chrome'
  | 'firefox'
  | 'edge'
  | 'samsung'
  | 'other';

export interface CameraPlatformContext {
  platformFamily: CameraPlatformFamily;
  browserFamily: CameraBrowserFamily;
  /** Major OS version when the user agent states it (iOS/Android only). */
  osVersionMajor: number | null;
  deviceType: 'mobile' | 'desktop';
  isIOSSafari: boolean;
  isIOSChrome: boolean;
  supportsVideoFrameCallback: boolean;
}

interface PlatformInputs {
  userAgent: string;
  maxTouchPoints: number;
  innerWidth: number;
}

const readInputs = (): PlatformInputs => ({
  userAgent: typeof navigator !== 'undefined' ? navigator.userAgent || '' : '',
  maxTouchPoints:
    typeof navigator !== 'undefined' && typeof navigator.maxTouchPoints === 'number'
      ? navigator.maxTouchPoints
      : 0,
  innerWidth: typeof window !== 'undefined' ? window.innerWidth : 0,
});

const parseMajor = (value: string | undefined): number | null => {
  if (!value) {
    return null;
  }
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

export function describeCameraPlatform(
  inputs: Partial<PlatformInputs> = {}
): CameraPlatformContext {
  const { userAgent, maxTouchPoints, innerWidth } = {
    ...readInputs(),
    ...inputs,
  };

  const isIPhoneFamily = /iPad|iPhone|iPod/.test(userAgent);
  // iPadOS 13+ reports a desktop Safari user agent; touch support is the
  // only reliable hint that WebKit-on-iOS camera behaviour applies.
  const isDesktopUserAgentIPad =
    /Macintosh/.test(userAgent) && maxTouchPoints > 1;
  const isIOS = isIPhoneFamily || isDesktopUserAgentIPad;
  const isAndroid = /Android/.test(userAgent);

  let platformFamily: CameraPlatformFamily = 'other';
  if (isIOS) {
    platformFamily = 'ios';
  } else if (isAndroid) {
    platformFamily = 'android';
  } else if (/Windows|Macintosh|Linux|CrOS/.test(userAgent)) {
    platformFamily = 'desktop';
  }

  let browserFamily: CameraBrowserFamily = 'other';
  if (/CriOS/.test(userAgent)) {
    browserFamily = 'chrome';
  } else if (/FxiOS/.test(userAgent)) {
    browserFamily = 'firefox';
  } else if (/EdgiOS|Edg\//.test(userAgent)) {
    browserFamily = 'edge';
  } else if (/SamsungBrowser/.test(userAgent)) {
    browserFamily = 'samsung';
  } else if (/Chrome\/|Chromium\//.test(userAgent)) {
    browserFamily = 'chrome';
  } else if (/Firefox\//.test(userAgent)) {
    browserFamily = 'firefox';
  } else if (/Safari\//.test(userAgent)) {
    browserFamily = 'safari';
  }

  let osVersionMajor: number | null = null;
  if (isIOS) {
    osVersionMajor = parseMajor(/OS (\d+)_/.exec(userAgent)?.[1]);
  } else if (isAndroid) {
    osVersionMajor = parseMajor(/Android (\d+)/.exec(userAgent)?.[1]);
  }

  const deviceType: 'mobile' | 'desktop' =
    isIOS || isAndroid || innerWidth < 600 ? 'mobile' : 'desktop';

  const supportsVideoFrameCallback =
    typeof HTMLVideoElement !== 'undefined' &&
    typeof (
      HTMLVideoElement.prototype as HTMLVideoElement & {
        requestVideoFrameCallback?: unknown;
      }
    ).requestVideoFrameCallback === 'function';

  return {
    platformFamily,
    browserFamily,
    osVersionMajor,
    deviceType,
    isIOSSafari: isIOS && browserFamily === 'safari',
    isIOSChrome: isIOS && browserFamily === 'chrome',
    supportsVideoFrameCallback,
  };
}

/**
 * Flat, telemetry-safe projection of the platform context. Every key is
 * deliberately free of the fragments the browser-telemetry sanitizer strips.
 */
export function getCameraPlatformTelemetry(): Record<string, string | number | boolean> {
  const context = describeCameraPlatform();
  return {
    platformFamily: context.platformFamily,
    browserFamily: context.browserFamily,
    osVersionMajor: context.osVersionMajor ?? -1,
    deviceType: context.deviceType,
    isIOSSafari: context.isIOSSafari,
    isIOSChrome: context.isIOSChrome,
    supportsVideoFrameCallback: context.supportsVideoFrameCallback,
  };
}
